import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import lockr from 'lockr';
import { handleError } from '../utils/helpers';
import { fetchApi, fetchApiAuth } from '../utils/api';
import { ResponseProps } from './types';
import constants from '../constants';

const {
    LOCALSTORAGE_EXPIRES_KEY,
    AUTH_KEY,
} = constants;

export const registerUser = createAsyncThunk(
    'user/register',
    async (payload:{email: string; password: string; first_name: string; last_name: string;}) => {
        const { email, password, first_name, last_name } = payload;
        const response:ResponseProps = await fetchApi({
            method: 'POST',
            url: 'register',
            body: {
                email,
                password,
                first_name,
                last_name,
            },
        });
        return response.data;
    },
);

export const loginUser = createAsyncThunk(
    'user/login',
    async (payload:{email: string; password: string;}, { rejectWithValue }) => {
        const { email, password } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'login',
                body: { email, password },
            });
            if (response.data.user) {
                const authDate = new Date();
                authDate.setMinutes(authDate.getMinutes() + 30);
                lockr.set(LOCALSTORAGE_EXPIRES_KEY, authDate);
                // WP7 (W1): the access token is delivered in an HttpOnly cookie,
                // not the body — store only a non-sensitive "logged in" flag.
                lockr.set(AUTH_KEY, true);
            } else if (response.data.twoFactorToken) {
                lockr.set('twoFactorToken', response.data.twoFactorToken);
            }
            return response.data;
        } catch (error:unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const forgotPassword = createAsyncThunk(
    'user/forgotPassword',
    async (payload:{email: string;}, { rejectWithValue }) => {
        const { email } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'forgot-password',
                body: { email },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const resetPassword = createAsyncThunk(
    'user/resetPassword',
    async (payload:{email: string|undefined; password: string; passwordConfirmation: string; token: string|undefined;}, { rejectWithValue }) => {
        const { email, password, passwordConfirmation, token } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'reset-password',
                body: {
                    email,
                    password,
                    password_confirmation: passwordConfirmation,
                    token,
                },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const setPassword = createAsyncThunk(
    'user/setPassword',
    async (payload:{email: string|undefined; password: string; passwordConfirmation: string; token: string|undefined;}, { rejectWithValue }) => {
        const { email, password, passwordConfirmation, token } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'set-password',
                body: {
                    email,
                    password,
                    password_confirmation: passwordConfirmation,
                    token,
                },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const logoutUser = createAsyncThunk(
    'user/logout',
    async (payload, { rejectWithValue }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'logout',
            });
            lockr.rm(AUTH_KEY);
            lockr.rm(LOCALSTORAGE_EXPIRES_KEY);
            localStorage.clear();
            return response.data;
        } catch (error:unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const twoFactorConfirmation = createAsyncThunk(
    'user/twoFactorConfirmation',
    async (payload:{code: string; callback: () => void;}, { rejectWithValue }) => {
        const { code, callback } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'two-factor-confirmation',
                body: {
                    twoFactorToken: lockr.get('twoFactorToken'),
                    code,
                },
            });

            if (response.data.token) {
                const authDate = new Date();
                authDate.setMinutes(authDate.getMinutes() + 30);
                lockr.set(LOCALSTORAGE_EXPIRES_KEY, authDate);
                lockr.set(AUTH_KEY, response.data.token);

                callback();
            }
            return response.data;
        } catch (error:unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const twoFactorResend = createAsyncThunk(
    'user/twoFactorResend',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await fetchApi({
                method: 'POST',
                url: 'two-factor-resend',
                body: {
                    twoFactorToken: lockr.get('twoFactorToken'),
                },
            });
            return response.data;
        } catch (error:unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);
